import React from 'react';
import { isAfter, isToday, isFuture } from 'date-fns';
export const getPlagiarismDetails = (plagiarismCount) => {
    if (plagiarismCount <= 10) {
        const text = (React.createElement("span", null,
            "Looks good! Honesty and original content are the best policies.",
            React.createElement("span", { role: "img", "aria-label": "smile-emoji" }, "\uD83D\uDE03"),
            "Automated tools are not great at detecting advanced forms of plagiarism though.",
            React.createElement("a", { target: "_blank", rel: "noreferrer", href: "https://peppercontent.freshdesk.com/support/solutions/articles/67000675333-content-audit-plagiarism-score" }, "Read our guidelines here.")));
        return {
            color: 'green',
            sign: 'tick',
            text
        };
    }
    else if (plagiarismCount <= 25) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `Some phrases in your content seem plagiarized or overly generic. Sometimes this is inevitable, but we recommend keeping it below 5%.`
        };
    }
    else if (plagiarismCount <= 40) {
        return {
            color: 'orange',
            sign: 'exclamation',
            text: `Quite a bit of this content seems to match other sources on the web. Generally, this should be less than 5%.`
        };
    }
    return {
        color: 'red',
        sign: 'exclamation',
        text: `Something is wrong here. Can you check if you've not copied content from another source? Generally, this should be less than 5%.`
    };
};
export const getGrammarDetails = (grammarCount) => {
    if (grammarCount <= 1) {
        return {
            color: 'green',
            sign: 'tick',
            text: `Nice work! We didn't find significant errors in your content.`
        };
    }
    else if (grammarCount <= 3) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `There are a few grammatical oversights (spelling, sentence structure) in your content. An error rate below 1% is generally acceptable.`
        };
    }
    else if (grammarCount <= 10) {
        return {
            color: 'orange',
            sign: 'exclamation',
            text: `Do you need to go back and make some changes? Great content relies on correctness and clarity, and an error rate below 1% is generally acceptable.`
        };
    }
    return {
        color: 'red',
        sign: 'exclamation',
        text: `Do you need to go back and make some changes? Great content relies on correctness and clarity, and an error rate below 1% is generally acceptable.`
    };
};
export const getReadabilityDetails = (readabilityCount) => {
    if (readabilityCount >= 90) {
        return {
            color: 'green',
            sign: 'tick',
            text: `This content is very easy to read`
        };
    }
    else if (readabilityCount >= 80) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `This content is easy to read`
        };
    }
    else if (readabilityCount >= 70) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `This content is fairly easy to read`
        };
    }
    else if (readabilityCount >= 60) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `This content is in plain english`
        };
    }
    else if (readabilityCount >= 50) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `This content is fairly difficult to read`
        };
    }
    else if (readabilityCount >= 30) {
        return {
            color: 'orange',
            sign: 'exclamation',
            text: `This content is difficult to read`
        };
    }
    else if (readabilityCount >= 10) {
        return {
            color: 'orange',
            sign: 'exclamation',
            text: `This content is very difficult to read. Best understood by university graduates.`
        };
    }
    return {
        color: 'red',
        sign: 'exclamation',
        text: `This content is extremely difficult to read. Best understood by university graduates.`
    };
};
export const getWordCountDetails = (wordCountPercentage, words) => {
    if (wordCountPercentage >= 20) {
        return {
            color: 'red',
            sign: 'exclamation',
            text: `The assignment requires an approximate word count of ${words}. Please add more content and submit the assignment again.`,
            header: `${wordCountPercentage}% below the required word count.`
        };
    }
    else if (wordCountPercentage > 10) {
        return {
            color: 'yellow',
            sign: 'exclamation',
            text: `The assignment requires an approximate word count of ${words}. Highly recommended to add more content here.`,
            header: `${wordCountPercentage}% below the required word count.`
        };
    }
    return null;
};
export const getWordCountTooltipText = (wordCount, maxCount) => {
    if (wordCount === 0)
        return '';
    if ((Math.abs(maxCount - wordCount) / maxCount) * 100 <= 10)
        return 'Great job! The word count is as per the provided assignment brief.';
    if (maxCount - wordCount > 0)
        return 'The word count is lesser than the required word count for this assignment. Try adding more words.';
    if (maxCount - wordCount < 0)
        return 'The word count is more than the required word count for this assignment. Try removing a few words.';
    return '';
};
export const getReadabilityScoreTooltipText = (readabilityScore) => {
    if (readabilityScore === undefined || readabilityScore === null)
        return '';
    if (readabilityScore >= 60)
        return 'Great job! Your content is easy to read.';
    if (readabilityScore < 60 && readabilityScore >= 30)
        return 'Your content is a bit difficult to read. Try using simpler words and sentences.';
    if (readabilityScore < 30)
        return 'Your content is extremely difficult to read. Try using simpler words and sentences.';
    return '';
};
export const getDeadlineTooltipText = (deadline, isAuditModal) => {
    if (deadline === '')
        return '';
    const isDeadlineExceeded = isAfter(new Date(), new Date(deadline));
    const isDeadlineToday = isToday(new Date(deadline));
    const isDeadlineInFuture = isFuture(new Date(deadline));
    if (isDeadlineExceeded) {
        return isAuditModal
            ? "You've exceeded the assignment deadline. From next time, try submitting the assignment on time."
            : "You've exceeded the assignment deadline.";
    }
    if (isDeadlineToday)
        return 'Hurry up! Your assignment is due for submission today.';
    if (isDeadlineInFuture)
        return 'Take your time. You still have time to complete the assignment.';
    return '';
};
export const getContentSuggestionsTooltipText = (suggestionsCount) => {
    if (suggestionsCount > 0)
        return 'You have a few suggestions to impove your content. Make sure to fix them before submitting the assignment for approval.';
    return '';
};
export const getPlagiarismTooltipText = (plagiarismPercent) => {
    if (plagiarismPercent >= 0 && plagiarismPercent <= 5)
        return 'Looks good! Honesty and original content are the best policies.';
    if (plagiarismPercent > 5)
        return 'Phrases in your content seem plagiarized or overly generic. We highly recommend keeping your plagiarism score below 5%.';
    return '';
};
export const getWordCountPillClass = (wordCount, maxCount) => {
    if (wordCount === 0)
        return 'Gray';
    const differencePercentage = (Math.abs(maxCount - wordCount) / maxCount) * 100;
    if (differencePercentage <= 10)
        return 'Gray';
    if (differencePercentage <= 20)
        return 'Yellow';
    if (differencePercentage > 20)
        return 'Red';
    return 'Gray';
};
export const getReadabilityScorePillClass = (readabilityScore) => {
    if (readabilityScore === undefined || readabilityScore === null)
        return 'Gray';
    if (readabilityScore >= 60)
        return 'Gray';
    if (readabilityScore < 60 && readabilityScore >= 30)
        return 'Yellow';
    if (readabilityScore < 30)
        return 'Red';
    return 'Gray';
};
export const getDeadlinePillClass = (deadline) => {
    if (deadline === '')
        return 'Gray';
    const isDeadlineExceeded = isAfter(new Date(), new Date(deadline));
    const isDeadlineToday = isToday(new Date(deadline));
    if (isDeadlineExceeded)
        return 'Red';
    if (isDeadlineToday)
        return 'Yellow';
    return 'Gray';
};
export const getContentSuggestionsPillClass = (suggestionsCount) => {
    if (suggestionsCount > 0)
        return 'Yellow';
    return 'Gray';
};
export const getPlagiarismPillClass = (plagiarismPercent) => {
    if (plagiarismPercent >= 0 && plagiarismPercent <= 5)
        return 'Gray';
    if (plagiarismPercent > 5)
        return 'Red';
    return 'Gray';
};
export const isEligibleForAudit = (existingAuditData, verticalName, language, role) => {
    var _a;
    if (!((_a = existingAuditData === null || existingAuditData === void 0 ? void 0 : existingAuditData.data) === null || _a === void 0 ? void 0 : _a.allowed_verticals))
        return false;
    if (!['writer', 'editor'].includes(role !== null && role !== void 0 ? role : ''))
        return false;
    if (language !== 'English')
        return false;
    if (!verticalName)
        return false;
    if (existingAuditData.data.allowed_verticals.indexOf(verticalName) === -1)
        return false;
    return true;
};
export const getAuditPropertyKeys = (auditData, role) => {
    var _a, _b, _c;
    if (!auditData)
        return ['writer_audits', 'writer_audit_limit'];
    const { data } = auditData;
    if (role === 'editor') {
        if ((_a = data === null || data === void 0 ? void 0 : data.editor_audits) === null || _a === void 0 ? void 0 : _a.length)
            return ['editor_audits', 'editor_audit_limit'];
        if ((_b = data === null || data === void 0 ? void 0 : data.writer_submission_audits) === null || _b === void 0 ? void 0 : _b.length)
            return ['writer_submission_audits', 'editor_audit_limit'];
        if ((_c = data === null || data === void 0 ? void 0 : data.writer_audits) === null || _c === void 0 ? void 0 : _c.length)
            return ['writer_audits', 'editor_audit_limit'];
    }
    return ['writer_audits', 'writer_audit_limit'];
};
//# sourceMappingURL=audits.js.map